/**
 * Shrinking a partner photo in the browser before it is uploaded (spec 21,
 * NFR-10.2.3 "compress/resize images before storage").
 *
 * No library: the browser already has everything this needs.
 *
 * 1. **Decode upright.** `createImageBitmap` with `imageOrientation:
 *    "from-image"` applies the EXIF rotation, so a portrait phone photo is not
 *    uploaded on its side.
 * 2. **Centre-crop to 4:3.** That is the frame of the member app's banner
 *    (`PhotoBanner`), which would crop to it anyway. Cropping here means the
 *    preview in this panel is exactly what members see, and the pixels that
 *    would be thrown away are never uploaded.
 * 3. **Resize to at most 1600×1200.** Sharp at 3x density on a phone-width
 *    banner, and the same edge the member app uses for FamilyBiz photos. Never
 *    upscales. A large reduction is done in halving steps, because one big
 *    `drawImage` step skips source pixels and shimmers on fine detail.
 * 4. **Re-encode as WebP**, or JPEG where the browser cannot encode WebP (its
 *    `toBlob` silently returns a PNG instead). Re-encoding also drops the EXIF
 *    block, so a phone's GPS position never reaches the CDN.
 * 5. **Stay under the API's 2 MB limit** by stepping the quality down before
 *    giving up. A real photo lands at a few hundred KB on the first try.
 *
 * The API still checks size and type from the bytes (NFR-10.3.7); this is an
 * optimisation, not a trust boundary.
 *
 * Spec 23 reuses this for hotline contact photos, which members see in a
 * circle: `{ aspect: 1, maxWidth: 800 }` gives a square instead of the 4:3
 * banner frame. The defaults are still the partner photo's.
 */

/** The API's `vendor_photo_max_bytes`. */
export const MAX_PHOTO_BYTES = 2 * 1024 * 1024;

const MAX_WIDTH = 1600;
const ASPECT = 4 / 3;

export type OptimizeOptions = {
  /** Width over height of the crop. Default 4:3, the partner banner. */
  aspect?: number;
  /** Longest edge after resizing. Never upscales. Default 1600. */
  maxWidth?: number;
};
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
const WEBP_QUALITIES = [0.8, 0.7, 0.6];
const JPEG_QUALITIES = [0.82, 0.72, 0.62];

export type OptimizedImage = {
  blob: Blob;
  /** `photo.webp` or `photo.jpg`, matching what was encoded. */
  fileName: string;
  width: number;
  height: number;
  /** Bytes before and after, for the "4.8 MB → 310 KB" note. */
  before: number;
  after: number;
};

export async function optimizePhoto(file: File, options: OptimizeOptions = {}): Promise<OptimizedImage> {
  const aspect = options.aspect ?? ASPECT;
  const maxWidth = options.maxWidth ?? MAX_WIDTH;
  if (!ACCEPTED.includes(file.type)) {
    throw new Error("Choose a JPEG, PNG or WebP image. iPhone HEIC photos need converting to JPEG first.");
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new Error("That image could not be opened. It may be damaged - try another file.");
  }

  try {
    const crop = centreCrop(bitmap.width, bitmap.height, aspect);
    const width = Math.min(maxWidth, crop.sw);
    const height = Math.round(width / aspect);
    const canvas = resample(bitmap, crop, width, height);
    const blob = await encode(canvas);
    return {
      blob,
      fileName: blob.type === "image/webp" ? "photo.webp" : "photo.jpg",
      width,
      height,
      before: file.size,
      after: blob.size,
    };
  } finally {
    bitmap.close();
  }
}

type Crop = { sx: number; sy: number; sw: number; sh: number };

function centreCrop(width: number, height: number, aspect: number): Crop {
  if (width / height > aspect) {
    const sw = Math.round(height * aspect);
    return { sx: Math.round((width - sw) / 2), sy: 0, sw, sh: height };
  }
  const sh = Math.round(width / aspect);
  return { sx: 0, sy: Math.round((height - sh) / 2), sw: width, sh };
}

function canvasOf(width: number, height: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser cannot resize images. Try Chrome, Edge or Firefox.");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  return [canvas, context];
}

function resample(bitmap: ImageBitmap, crop: Crop, width: number, height: number): HTMLCanvasElement {
  let source: CanvasImageSource = bitmap;
  let { sx, sy, sw, sh } = crop;

  // Halve while the next halving is still at least the target size.
  while (sw / 2 >= width) {
    const [step, context] = canvasOf(Math.round(sw / 2), Math.round(sh / 2));
    context.drawImage(source, sx, sy, sw, sh, 0, 0, step.width, step.height);
    source = step;
    sx = 0;
    sy = 0;
    sw = step.width;
    sh = step.height;
  }

  const [canvas, context] = canvasOf(width, height);
  context.drawImage(source, sx, sy, sw, sh, 0, 0, width, height);
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

async function encode(canvas: HTMLCanvasElement): Promise<Blob> {
  for (const quality of WEBP_QUALITIES) {
    const blob = await toBlob(canvas, "image/webp", quality);
    // A browser that cannot encode WebP hands back a PNG rather than failing.
    if (blob?.type !== "image/webp") return encodeJpeg(canvas);
    if (blob.size <= MAX_PHOTO_BYTES) return blob;
  }
  throw tooLarge();
}

async function encodeJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  for (const quality of JPEG_QUALITIES) {
    const blob = await toBlob(canvas, "image/jpeg", quality);
    if (blob && blob.size <= MAX_PHOTO_BYTES) return blob;
  }
  throw tooLarge();
}

function tooLarge(): Error {
  return new Error("That photo is still larger than 2 MB after compressing. Try a simpler or smaller image.");
}

/** `4812345` → `4.6 MB`, `317000` → `310 KB`. */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
